import {
    ref, watch, computed,
} from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useGrowersList() {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')
    const primaryRole = Array.isArray(userData.roles) && userData.roles.length > 0
        ? userData.roles[0]
        : null

    const toast = useToast()
    const records = ref(null)
    const rows = ref([])
    const isLoading = ref(false)
    const inFlightRequestKey = ref(null)
    const inFlightRequestPromise = ref(null)
    const activeLoadSequence = ref(0)
    const refetchScheduled = ref(false)

    const columns = [
        {
            key: 'name', label: 'Grower Name', sortable: true, searchable: true,
        },
        { key: 'gender', sortable: true, searchable: true },
        {
            key: 'referenceNumber', label: 'Ref #', sortable: true, searchable: true,
        },
        {
            key: 'registrationNumber', label: 'Reg #', sortable: true, searchable: true,
        },
        { key: 'type', sortable: true, searchable: true },
        {
            key: 'societyName', label: 'Society', sortable: true, searchable: true,
        },
        {
            key: 'sustainableVolume', label: 'Sustainable Vol', sortable: true, searchable: false, thClass: 'text-right', tdClass: 'text-right',
        },
        {
            key: 'lockedVolume', label: 'Locked Vol', sortable: true, searchable: false, thClass: 'text-right', tdClass: 'text-right',
        },
        {
            key: 'soldVolume', label: 'Sold Vol', sortable: true, searchable: false, thClass: 'text-right', tdClass: 'text-right',
        },
        'actions',
    ]

    const from = ref(0)
    const to = ref(0)
    const meta = ref({ from: 0, to: 0, total: 0 })
    const perPage = ref(10)
    const totalRecords = ref(0)
    const currentPage = ref(1)
    const perPageOptions = [5, 10, 25, 50, 100]
    const searchQuery = ref('')
    const sortBy = ref('name')
    const isSortDirDesc = ref(false)
    const isSuperUser = ref(
        userData.super_user === true
        || (primaryRole && (
            primaryRole.id === 1
            || (typeof primaryRole.title === 'string' && primaryRole.title.toLowerCase().includes('super'))
        )),
    )

    const getLocationId = location => {
        if (location === null || location === undefined) return null
        if (typeof location === 'object' && location.id !== undefined && location.id !== null) return location.id

        return location
    }

    const normalizeLocationLevel = location => {
        if (!location || typeof location !== 'object') {
            return ''
        }

        return `${location.level?.name || ''}`.trim().toLowerCase()
    }

    const isAreaSelection = location => {
        if (location === null || location === undefined) {
            return false
        }

        if (typeof location !== 'object') {
            return true
        }

        const hasChildren = Array.isArray(location.children) && location.children.length > 0

        return normalizeLocationLevel(location) === 'area' || !hasChildren
    }

    const defaultLocation = userData.station && userData.station.location
        ? userData.station.location
        : userData.location
    const defaultLocationId = getLocationId(defaultLocation)
    const locationFilter = ref(!isSuperUser.value && isAreaSelection(defaultLocation) ? defaultLocationId : null)
    const requiresLocationSelection = computed(() => !locationFilter.value)
    const emptyStateText = computed(() => {
        if (requiresLocationSelection.value) return 'Select a region and area to load growers.'
        if (isLoading.value) return 'Loading growers...'

        return 'No records found'
    })

    const getLocationHierarchy = location => {
        if (!location || typeof location !== 'object') {
            return []
        }

        const selectedLocations = []
        let currentLocation = location

        while (currentLocation) {
            if (currentLocation.level && currentLocation.id) {
                selectedLocations.push({
                    level: currentLocation.level.name.toLowerCase(),
                    id: currentLocation.id,
                })
            }

            currentLocation = currentLocation.parent
        }

        return selectedLocations
    }

    const defaultSelectedLocations = ref(isSuperUser.value ? [] : getLocationHierarchy(defaultLocation))

    const normalizeText = value => {
        if (value === null || value === undefined) {
            return ''
        }

        return `${value}`.trim()
    }

    const normalizeGrowerId = value => {
        if (value === null || value === undefined || value === '') return null

        const growerId = Number(value)

        if (Number.isFinite(growerId)) {
            return growerId
        }

        return normalizeText(value)
    }

    const toVolumeNumber = value => {
        const volume = Number(value)

        return Number.isFinite(volume) ? volume : 0
    }

    const normalizeGender = value => {
        const gender = normalizeText(value)

        if (!gender) {
            return ''
        }

        return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()
    }

    const normalizeGrowerVolumeItem = payload => {
        const growerId = normalizeGrowerId(payload?.grower_id ?? payload?.growerId ?? payload?.id)

        return {
            id: growerId,
            growerId,
            name: normalizeText(payload?.grower_name ?? payload?.growerName ?? payload?.name),
            gender: normalizeGender(payload?.gender),
            reference: normalizeText(payload?.reference ?? payload?.reference_number ?? payload?.referenceNumber),
            referenceNumber: normalizeText(payload?.reference_number ?? payload?.referenceNumber ?? payload?.reference),
            registrationNumber: normalizeText(payload?.registration_number ?? payload?.registrationNumber),
            type: normalizeText(payload?.type).toUpperCase(),
            societyName: normalizeText(payload?.society_name ?? payload?.societyName ?? payload?.society?.name),
            lockedVolume: toVolumeNumber(payload?.locked_volume ?? payload?.locked_vol ?? payload?.lockedVolume),
            sustainableVolume: toVolumeNumber(payload?.sustainable_volume ?? payload?.sustainable_max_vol ?? payload?.sustainableVolume ?? payload?.sustainable_vol),
            soldVolume: toVolumeNumber(payload?.sold_volume ?? payload?.soldVolume),
        }
    }

    const extractGrowerVolumeItems = payload => {
        if (Array.isArray(payload)) {
            return payload
        }

        if (!payload || typeof payload !== 'object') {
            return []
        }

        if (payload.growers !== undefined) {
            return extractGrowerVolumeItems(payload.growers)
        }

        if (payload.grower_volume !== undefined) {
            return extractGrowerVolumeItems(payload.grower_volume)
        }

        if (payload.grower_volumes !== undefined) {
            return extractGrowerVolumeItems(payload.grower_volumes)
        }

        if (payload.data !== undefined && payload.data !== payload) {
            return extractGrowerVolumeItems(payload.data)
        }

        if (Array.isArray(payload.grower_volume)) {
            return payload.grower_volume
        }

        if (Array.isArray(payload.grower_volumes)) {
            return payload.grower_volumes
        }

        if (Array.isArray(payload.data)) {
            return payload.data
        }

        if (Array.isArray(payload.growers)) {
            return payload.growers
        }

        if (Array.isArray(payload.rows)) {
            return payload.rows
        }

        if (Array.isArray(payload.records)) {
            return payload.records
        }

        if (payload.grower_id !== undefined || payload.growerId !== undefined || payload.id !== undefined) {
            return [payload]
        }

        return []
    }

    const resolveMetaSource = payload => {
        if (!payload || typeof payload !== 'object') {
            return {}
        }

        return payload.meta
            || payload.pagination
            || payload.growers?.meta
            || payload.growers?.pagination
            || payload.grower_volumes?.meta
            || payload.grower_volumes?.pagination
            || payload.grower_volume?.meta
            || payload.grower_volume?.pagination
            || payload.growers
            || payload.grower_volumes
            || payload.grower_volume
            || payload
    }

    const hasServerMeta = payload => {
        const responseMeta = resolveMetaSource(payload)

        return !!responseMeta && (
            responseMeta.total !== undefined
            || responseMeta.from !== undefined
            || responseMeta.to !== undefined
        )
    }

    const normalizeMeta = (payload, rowCount) => {
        const responseMeta = resolveMetaSource(payload)
        const total = Number(responseMeta.total ?? rowCount) || 0
        const fromValue = responseMeta.from ?? (total === 0 ? 0 : ((currentPage.value - 1) * perPage.value) + 1)
        const toValue = responseMeta.to ?? Math.min(currentPage.value * perPage.value, total)

        return {
            from: Number(fromValue) || 0,
            to: Number(toValue) || 0,
            total,
        }
    }

    const createClientMeta = rowCount => ({
        from: rowCount === 0 ? 0 : Math.min((((currentPage.value - 1) * perPage.value) + 1), rowCount),
        to: Math.min(currentPage.value * perPage.value, rowCount),
        total: rowCount,
    })

    const searchableFields = ['name', 'gender', 'reference', 'referenceNumber', 'registrationNumber', 'type', 'societyName']

    const filterRows = sourceRows => {
        const query = normalizeText(searchQuery.value).toLowerCase()

        if (!query) {
            return sourceRows
        }

        return sourceRows.filter(row => searchableFields.some(field => normalizeText(row[field]).toLowerCase().includes(query)))
    }

    const sortRows = sourceRows => {
        const activeSortBy = sortBy.value

        if (!activeSortBy) {
            return sourceRows
        }

        return [...sourceRows].sort((left, right) => {
            const leftValue = left[activeSortBy]
            const rightValue = right[activeSortBy]

            if (leftValue === rightValue) {
                return 0
            }

            if (leftValue === null || leftValue === undefined || leftValue === '') {
                return isSortDirDesc.value ? 1 : -1
            }

            if (rightValue === null || rightValue === undefined || rightValue === '') {
                return isSortDirDesc.value ? -1 : 1
            }

            const normalizedLeft = typeof leftValue === 'string' ? leftValue.toLowerCase() : leftValue
            const normalizedRight = typeof rightValue === 'string' ? rightValue.toLowerCase() : rightValue

            if (normalizedLeft > normalizedRight) {
                return isSortDirDesc.value ? -1 : 1
            }

            return isSortDirDesc.value ? 1 : -1
        })
    }

    const paginateRows = sourceRows => {
        const start = (currentPage.value - 1) * perPage.value

        return sourceRows.slice(start, start + perPage.value)
    }

    const resolveRequestLocationId = () => {
        if (locationFilter.value !== null && locationFilter.value !== undefined) {
            return locationFilter.value
        }

        return null
    }

    const mapSortFieldForRequest = sortField => {
        const sortFieldMap = {
            name: 'grower_name',
            gender: 'gender',
            referenceNumber: 'reference_number',
            registrationNumber: 'registration_number',
            type: 'type',
            societyName: 'society_name',
            sustainableVolume: 'sustainable_max_vol',
            lockedVolume: 'locked_volume',
            soldVolume: 'sold_volume',
        }

        return sortFieldMap[sortField] || sortField
    }

    const resetTableState = () => {
        rows.value = []
        meta.value = createClientMeta(0)
        totalRecords.value = 0
        from.value = 0
        to.value = 0
    }

    const applyTableState = (tableRows, tableMeta) => {
        rows.value = tableRows
        meta.value = tableMeta
        totalRecords.value = tableMeta.total
        from.value = tableMeta.from
        to.value = tableMeta.to
    }

    const loadRows = async () => {
        const loadSequence = activeLoadSequence.value + 1
        activeLoadSequence.value = loadSequence
        let requestPromise = null

        if (requiresLocationSelection.value) {
            resetTableState()
            return
        }

        try {
            const activeLocationId = resolveRequestLocationId()

            if (!activeLocationId) {
                resetTableState()
                return
            }

            const requestParams = {
                location_id: activeLocationId,
                query: searchQuery.value,
                per_page: perPage.value,
                page: currentPage.value,
                sortBy: mapSortFieldForRequest(sortBy.value),
                sortDesc: isSortDirDesc.value,
            }
            const requestKey = JSON.stringify(requestParams)

            if (inFlightRequestKey.value === requestKey && inFlightRequestPromise.value) {
                await inFlightRequestPromise.value
                return
            }

            isLoading.value = true

            requestPromise = (async () => {
                const response = await store.dispatch('cromis-grower/growerVolumes', requestParams)
                const normalizedRows = extractGrowerVolumeItems(response.data).map(normalizeGrowerVolumeItem)

                if (loadSequence !== activeLoadSequence.value) {
                    return
                }

                if (hasServerMeta(response.data)) {
                    applyTableState(normalizedRows, normalizeMeta(response.data, normalizedRows.length))
                    return
                }

                const filteredRows = filterRows(normalizedRows)
                const sortedRows = sortRows(filteredRows)
                applyTableState(paginateRows(sortedRows), createClientMeta(sortedRows.length))
            })()

            inFlightRequestKey.value = requestKey
            inFlightRequestPromise.value = requestPromise

            await requestPromise
        } catch (error) {
            if (loadSequence !== activeLoadSequence.value) {
                return
            }

            resetTableState()
            toast({
                component: ToastificationContent,
                props: {
                    title: 'Error fetching grower volume list',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                },
            })
        } finally {
            if (requestPromise && inFlightRequestPromise.value === requestPromise) {
                inFlightRequestKey.value = null
                inFlightRequestPromise.value = null
            }

            if (loadSequence === activeLoadSequence.value) {
                isLoading.value = false
            }
        }
    }

    const refetch = async () => {
        await loadRows()
    }

    const scheduleRefetch = () => {
        if (refetchScheduled.value) {
            return
        }

        refetchScheduled.value = true

        Promise.resolve().then(async () => {
            refetchScheduled.value = false
            await refetch()
        })
    }

    watch([locationFilter, perPage, searchQuery, sortBy, isSortDirDesc], () => {
        if (currentPage.value !== 1) {
            currentPage.value = 1
            return
        }

        scheduleRefetch()
    }, { immediate: true })

    watch(currentPage, () => {
        scheduleRefetch()
    })

    const resolveGrowerTypeVariant = growerType => {
        const normalizedType = normalizeText(growerType).toUpperCase()

        if (normalizedType === 'T') return 'success'
        if (normalizedType === 'S') return 'primary'
        return 'secondary'
    }

    const resolveStatusVariant = status => {
        if (status === 1) return 'success'
        if (status === 0) return 'secondary'
        return 'warning'
    }

    return {
        rows,
        isLoading,
        columns,
        perPage,
        currentPage,
        totalRecords,
        from,
        to,
        meta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        records,
        resolveGrowerTypeVariant,
        resolveStatusVariant,
        refetch,
        locationFilter,
        isSuperUser,
        requiresLocationSelection,
        emptyStateText,
        defaultSelectedLocations,
    }
}
